import { type Node as PMNode, Slice } from 'prosemirror-model';
import { suggestionTransactionKey } from 'prosemirror-suggestion-mode';
import type { EditorView } from 'prosemirror-view';

import { schema } from '../schema';
import { htmlToDoc } from '../schemaSerializer';
import type { BlockDiff, DiffOp } from './blockDiffTypes';

export type ApplySuggestionsArgs = {
  blockDiffs: BlockDiff[];
  username: string;
};

export function applyTargetHtmlAsSuggestions(
  view: EditorView,
  { blockDiffs, username }: ApplySuggestionsArgs,
): number {
  const cleaned = dropIdenticalBlockReplacements(blockDiffs, view.state.doc);
  const sorted = [...cleaned].sort((a, b) => b.blockIndex - a.blockIndex);
  let applied = 0;

  for (const diff of sorted) {
    if (diff.type === 'unchanged') continue;
    const tr = view.state.tr;
    tr.setMeta(suggestionTransactionKey, {
      inSuggestionMode: true,
      username,
      data: diff.summary ? { reason: diff.summary } : {},
      skipSuggestionOperation: false,
    });

    const range = findBlockRange(view.state.doc, diff.blockIndex);

    if (diff.type === 'deleteBlock') {
      if (!range) continue;
      tr.delete(range.from, range.to);
      view.dispatch(tr);
      applied += 1;
      continue;
    }

    if (diff.type === 'insertBlock') {
      const newDoc = htmlToDoc(diff.html);
      if (!newDoc.firstChild) continue;
      const insertAt = range ? range.from : view.state.doc.content.size;
      tr.insert(insertAt, newDoc.firstChild);
      view.dispatch(tr);
      applied += 1;
      continue;
    }

    if (diff.type === 'textDiff') {
      if (!range) continue;
      const block = view.state.doc.nodeAt(range.from);
      if (!block) continue;
      const meaningfulOps = dropNoOpReplacements(diff.operations, block.textContent);
      if (meaningfulOps.length === 0) continue;
      // Build a text-offset → PM-position map by walking the block's text nodes.
      // For nested content (lists, tables, blockquote) PM positions don't match
      // raw textContent indices, so we have to translate via the actual layout.
      const offsetMap = buildTextOffsetMap(block, range.from);
      // Sort ops descending so earlier mutations don't shift later positions.
      const ops = [...meaningfulOps].sort((a, b) => offsetOf(b) - offsetOf(a));

      for (const op of ops) {
        if (op.type === 'delete') {
          const from = mapTextOffset(offsetMap, op.start);
          const to = mapTextOffset(offsetMap, op.end);
          if (from === null || to === null || to <= from) continue;
          tr.delete(from, to);
        } else if (op.type === 'insert') {
          if (!op.text) continue;
          const at = mapTextOffset(offsetMap, op.pos);
          if (at === null) continue;
          tr.insert(at, schema.text(op.text));
        }
      }
      if (tr.steps.length > 0) {
        view.dispatch(tr);
        applied += 1;
      }
      continue;
    }
  }

  return applied;
}

function dropIdenticalBlockReplacements(
  blockDiffs: BlockDiff[],
  doc: PMNode,
): BlockDiff[] {
  const blockTexts = collectBlockTexts(doc);
  const skip = new Set<number>();

  // Pair deleteBlock + adjacent insertBlock where stripped text matches.
  for (let i = 0; i < blockDiffs.length; i += 1) {
    const diff = blockDiffs[i];
    if (diff.type !== 'deleteBlock') continue;
    const oldText = (blockTexts[diff.blockIndex] ?? '').trim();
    if (!oldText) continue;
    for (let j = 0; j < blockDiffs.length; j += 1) {
      if (i === j || skip.has(j)) continue;
      const other = blockDiffs[j];
      if (other.type !== 'insertBlock') continue;
      if (Math.abs(other.blockIndex - diff.blockIndex) > 1) continue;
      if (stripHtml(other.html) === oldText) {
        skip.add(i);
        skip.add(j);
        break;
      }
    }
  }

  return blockDiffs.filter((_, idx) => !skip.has(idx));
}

function collectBlockTexts(doc: PMNode): string[] {
  const texts: string[] = [];
  doc.forEach((node) => {
    if (node.isBlock) texts.push(node.textContent);
  });
  return texts;
}

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? '').trim();
}

function dropNoOpReplacements(ops: DiffOp[], blockText: string): DiffOp[] {
  // Pair adjacent delete+insert ops that span the same range and replace text
  // with itself; skip those entirely.
  const result: DiffOp[] = [];
  const pendingDeletes = new Map<string, number>();
  for (let i = 0; i < ops.length; i += 1) {
    const op = ops[i];
    if (op.type === 'delete') {
      const deleted = blockText.slice(op.start, op.end);
      pendingDeletes.set(`${op.start}|${op.end}|${deleted}`, i);
    }
  }
  for (let i = 0; i < ops.length; i += 1) {
    const op = ops[i];
    if (op.type === 'insert') {
      let canceled = false;
      for (const [key] of pendingDeletes) {
        const [, , deletedText] = key.split('|');
        if (deletedText === op.text) {
          pendingDeletes.delete(key);
          canceled = true;
          break;
        }
      }
      if (!canceled) result.push(op);
    } else {
      const deleted = blockText.slice(op.start, op.end);
      const stillPending = pendingDeletes.has(`${op.start}|${op.end}|${deleted}`);
      if (stillPending) result.push(op);
    }
  }
  return result;
}

/**
 * Map a plain-text character offset (within `block.textContent`) to an absolute
 * PM document position. Walks every text node in the block and tracks the
 * cumulative text offset alongside its PM position. Handles nested content
 * (lists, tables, blockquote, marks) where text indices and PM positions
 * diverge by node-boundary tokens.
 *
 * Returns an array of segments: each segment is a contiguous text node plus
 * its starting PM position and starting text offset. Lookup is O(log n) via
 * binary search.
 */
type OffsetSegment = {
  textStart: number;
  textEnd: number;
  pmStart: number;
};

function buildTextOffsetMap(block: PMNode, blockPmStart: number): OffsetSegment[] {
  const segments: OffsetSegment[] = [];
  let textOffset = 0;
  // descendants() yields offsets relative to the block's content frame.
  // Absolute PM position of a child at `pos` is blockPmStart + 1 + pos.
  block.descendants((node, pos) => {
    if (!node.isText) return true;
    const length = node.text?.length ?? 0;
    if (length === 0) return false;
    segments.push({
      textStart: textOffset,
      textEnd: textOffset + length,
      pmStart: blockPmStart + 1 + pos,
    });
    textOffset += length;
    return false;
  });
  // Append a sentinel for the block's end so offsets at the very end map to
  // the position just after the last text node (useful for trailing inserts).
  segments.push({
    textStart: textOffset,
    textEnd: textOffset,
    pmStart: blockPmStart + block.nodeSize - 1,
  });
  return segments;
}

function mapTextOffset(segments: OffsetSegment[], textOffset: number): number | null {
  if (segments.length === 0) return null;
  for (const seg of segments) {
    if (textOffset >= seg.textStart && textOffset <= seg.textEnd) {
      return seg.pmStart + (textOffset - seg.textStart);
    }
  }
  // Beyond last segment — clamp to end sentinel.
  const last = segments[segments.length - 1];
  return last.pmStart;
}

function findBlockRange(doc: PMNode, blockIndex: number): { from: number; to: number } | null {
  let count = 0;
  let result: { from: number; to: number } | null = null;
  doc.forEach((node, offset) => {
    if (node.isBlock) {
      if (count === blockIndex) {
        result = { from: offset, to: offset + node.nodeSize };
      }
      count += 1;
    }
  });
  return result;
}

function offsetOf(op: { type: string; start?: number; pos?: number }): number {
  return op.type === 'delete' ? (op.start ?? 0) : (op.pos ?? 0);
}

// Kept for tests that check the diff conversion logic — left as-is for now,
// but applyTargetHtmlAsSuggestions no longer uses it.
export function blockDiffsToTextSuggestions(_doc: PMNode, _blockDiffs: BlockDiff[]): never[] {
  return [];
}

// Re-export to allow consumers to keep importing Slice if needed
export { Slice };
